import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { buildSkills, findCompaniesByTechnology as findCompaniesUtil } from '../utils/cvUtils.js';

const CvDataContext = createContext(null);

export const CvDataProvider = ({ children, cvName = 'default' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Stable ref for cvName so load callback doesn't change on every render
  const cvNameRef = useRef(cvName);
  cvNameRef.current = cvName;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const name = cvNameRef.current;
      const apiUrl = process.env.NODE_ENV === 'development'
        ? `http://localhost:8888/.netlify/functions/cv?name=${name}`
        : `/.netlify/functions/cv?name=${name}`;
      const res = await fetch(apiUrl);
      const contentType = res.headers.get('content-type') || '';
      const parseBody = async () => (contentType.includes('application/json') ? await res.json() : await res.text());
      if (!res.ok) {
        const body = await parseBody();
        const details = typeof body === 'string' ? body : body?.error || JSON.stringify(body);
        throw new Error(`Failed to load CV (HTTP ${res.status})${details ? `: ${details}` : ''}`);
      }
      const json = await res.json();
      setData(json);
      setLastUpdated(Date.now());
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []); // stable — never recreated

  useEffect(() => {
    load();
  }, [load, cvName]);

  const experience = useMemo(() => data?.experience || [], [data]);

  const skills = useMemo(() => buildSkills(experience), [experience]);

  const findCompaniesByTechnology = useCallback((technology) =>
    findCompaniesUtil(experience, technology), [experience]);

  const value = useMemo(() => ({
    loading,
    error,
    reload: load,
    lastUpdated,
    personalInfo: data?.personalInfo,
    contactInfo: data?.contactInfo,
    hobbies: data?.hobbies || [],
    education: data?.education || [],
    patents: data?.patents || [],
    experience,
    mainMedia: data?.mainMedia || [],
    skills,
    findCompaniesByTechnology,
  }), [loading, error, data, skills, lastUpdated, load, findCompaniesByTechnology, experience]);

  return (
    <CvDataContext.Provider value={value}>
      {children}
    </CvDataContext.Provider>
  );
};

export const useCvData = () => {
  const ctx = useContext(CvDataContext);
  if (!ctx) throw new Error('useCvData must be used within CvDataProvider');
  return ctx;
};
